import { create } from "zustand";
import {
  EMPTY_TITLE_FILTERS,
  type KeywordMatch,
  type TobisTitle,
  type TobisTitleFilters,
  type TobisTitleSort,
} from "@/lib/tobis";

// Der Stand des Titel-Registers: Filter, Sortierung UND die geladene
// Seitenfolge.
//
// Warum ein Store und kein useState in der Seite: wer einen Titel oeffnet und
// zurueckgeht, mountet die Liste neu - mit useState waren Suche, Filter und
// alle nachgeladenen Seiten weg, und bei 545.000 Titeln ist das Wiederfinden
// der Stelle echte Arbeit. Der Store haelt den Stand ueber die Navigation.
//
// Warum nicht die URL: der Filterstand alleine passt dort hinein, die geladenen
// Seiten nicht. Ein Zurueck haette also weiter neu geladen und bei Seite 1
// angefangen. `?search=` bleibt als Eingang von aussen bestehen (Titel-Link aus
// einer Filmografie), siehe TobisTitlesPage.
//
// Bewusst NICHT persistiert (anders als ui-store): der Bestand aendert sich mit
// jedem Ingest-Lauf, eine ueber Tage konservierte Trefferliste waere irgendwann
// gelogen. Der Stand soll eine Sitzung ueberleben, nicht einen Neustart.

interface TobisRegisterState {
  filters: TobisTitleFilters;
  sort: TobisTitleSort;
  /**
   * Ausgewaehlte Tonalitaets-Keywords (siehe TobisKeywordFilter). Getrennt von
   * TobisTitleFilters, weil es im BFF ein Rang-Signal ist, kein
   * Ausschlusskriterium, und sich mit Cursor-Paging ausschliesst (siehe
   * routes/tobis.ts) - nicht Teil der "Filter zuruecksetzen"-Taste.
   */
  keywords: KeywordMatch[];
  /**
   * false (Default) = mindestens eines der gewaehlten Keywords zaehlt schon
   * ("oder"), true = Volltreffer (alle gewaehlten Keywords) ruecken strikt
   * vor jeden Teiltreffer ("und") - siehe TitleFilters.keywordMatchAll.
   * Aendert nur die Rangfolge, kein Ausschlusskriterium.
   */
  keywordMatchAll: boolean;
  titles: TobisTitle[];
  /** Cursor der zuletzt geladenen Seite. null heisst "es gibt nichts mehr". */
  cursor: string | null;
  /** Gesamtzahl passend zum Filterstand, vom BFF nur auf der ersten Seite
   *  mitgeliefert (siehe fetchTitles/countTitles) - null vor dem ersten Laden
   *  oder wenn der Server sie nicht mitschickte (z.B. Tonalitaets-Rang-Modus). */
  total: number | null;
  /**
   * Ob `titles` zum aktuellen Filter- und Sortierstand gehoert. false zwingt
   * die Seite zum Nachladen - das ist die einzige Stelle, an der "muss ich
   * laden?" entschieden wird.
   */
  loaded: boolean;

  setFilters: (next: TobisTitleFilters) => void;
  setSort: (next: TobisTitleSort) => void;
  setKeywords: (next: KeywordMatch[]) => void;
  setKeywordMatchAll: (next: boolean) => void;
  /** Erste Seite zu einem Filterstand. */
  setPage: (titles: TobisTitle[], cursor: string | null, total?: number | null) => void;
  /** Naechste Seite anhaengen statt ersetzen. */
  appendPage: (titles: TobisTitle[], cursor: string | null) => void;
  /** Nach einem Fehlschlag: nicht in einer Endlos-Ladeschleife haengen bleiben. */
  markLoaded: () => void;
}

export const useTobisRegisterStore = create<TobisRegisterState>((set) => ({
  filters: EMPTY_TITLE_FILTERS,
  sort: "release",
  keywords: [],
  keywordMatchAll: false,
  titles: [],
  cursor: null,
  total: null,
  loaded: false,

  // Jede Aenderung an Filter, Sortierung oder Keyword-Auswahl verwirft die
  // geladene Seitenfolge: die Cursor-Form haengt an der Sortierung (und
  // Keywords schalten in den cursorlosen Rang-Modus um), eine Trefferliste zu
  // einem alten Stand waere schlicht falsch. total faellt aus demselben Grund
  // weg, es gilt nur fuer den Filterstand, der es lieferte.
  setFilters: (next) => set({ filters: next, loaded: false, titles: [], cursor: null, total: null }),
  setSort: (next) => set({ sort: next, loaded: false, titles: [], cursor: null, total: null }),
  setKeywords: (next) => set({ keywords: next, loaded: false, titles: [], cursor: null, total: null }),
  setKeywordMatchAll: (next) => set({ keywordMatchAll: next, loaded: false, titles: [], cursor: null, total: null }),

  setPage: (titles, cursor, total) => set({ titles, cursor, loaded: true, total: total ?? null }),
  // total bleibt unangetastet: appendPage haengt nur weitere Seiten AN, die
  // Gesamtzahl aus der ersten Seite gilt unveraendert weiter.
  appendPage: (titles, cursor) => set((s) => ({ titles: [...s.titles, ...titles], cursor })),
  markLoaded: () => set({ loaded: true }),
}));
